// src/pages/TeacherMonthlyLessonsReport.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { useUser } from '../contexts/UserContext';
import { useLessons } from '../contexts/LessonsContext';
import TeacherMonthlyReportHeader from '../components/ui/TeacherMonthlyReportHeader';
import MonthPicker from '../components/ui/Monthpicker';
import SpinnerOverlay from '../components/ui/SpinnerOverlay';

/* ===================== Helpers ===================== */
const pad2 = (n) => String(n).padStart(2, '0');

const money = (n) =>
  Number(n || 0).toLocaleString('he-IL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return 'תאריך לא תקין';
  return d.toLocaleDateString('he-IL', { weekday: 'short', day: 'numeric', month: 'short' });
};

/* ===================== Small UI atoms ===================== */
function MoneyCell({ value, color, bold = false }) {
  const styles = {
    blue:    'bg-blue-50 text-blue-700 border-blue-100',
    green:   'bg-green-50 text-green-700 border-green-100',
    amber:   'bg-amber-50 text-amber-700 border-amber-100',
    rose:    'bg-rose-50 text-rose-700 border-rose-100',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    purple:  'bg-purple-50 text-purple-700 border-purple-100',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs border whitespace-nowrap ${bold ? 'font-bold' : 'font-semibold'} ${styles[color]}`}>
      ₪{value}
    </span>
  );
}

function StatusBadge({ status, label }) {
  const styles = {
    DONE:      'bg-emerald-50 text-emerald-700 border-emerald-200',
    UPCOMING:  'bg-blue-50 text-blue-700 border-blue-200',
    CANCELLED: 'bg-rose-50 text-rose-700 border-rose-200',
    NO_SHOW:   'bg-amber-50 text-amber-700 border-amber-200',
    UNKNOWN:   'bg-gray-50 text-gray-700 border-gray-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border whitespace-nowrap ${styles[status] || styles.UNKNOWN}`}>
      {label}
    </span>
  );
}

// Shown wherever a supplier-course lesson's net-to-pay would otherwise
// just read "₪0.00" with no explanation — this is expected, not a
// missing payment: compensation for these happens directly between
// the teacher and the supplier who owns the course, outside the
// platform's commission/tax pipeline entirely.
function SupplierPaidBadge({ compact = false }) {
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border whitespace-nowrap bg-slate-50 text-slate-600 border-slate-200"
      title="שיעור בתוכנית של ספק — התשלום עבורו מתבצע ישירות מול הספק, מחוץ למערכת"
    >
      {compact ? 'ע״י הספק' : 'משולם ע״י הספק'}
    </span>
  );
}

const getRowStatusClass = (status) => {
  switch (status) {
    case 'DONE':      return 'hover:bg-emerald-50/50';
    case 'UPCOMING':  return 'hover:bg-blue-50/50';
    case 'CANCELLED': return 'hover:bg-rose-50/50';
    case 'NO_SHOW':   return 'hover:bg-amber-50/50';
    default:          return 'hover:bg-gray-50';
  }
};

const getStatusAccentBarClass = (status) => {
  switch (status) {
    case 'DONE':      return 'bg-gradient-to-r from-emerald-400 to-green-500';
    case 'UPCOMING':  return 'bg-gradient-to-r from-blue-400 to-indigo-500';
    case 'CANCELLED': return 'bg-gradient-to-r from-rose-400 to-red-500';
    case 'NO_SHOW':   return 'bg-gradient-to-r from-amber-400 to-orange-500';
    default:          return 'bg-gradient-to-r from-gray-400 to-gray-500';
  }
};

const getReportStatusLabel = (lesson) => {
  switch (lesson.reportStatus) {
    case 'DONE':      return 'בוצע';
    case 'UPCOMING':  return 'טרם התקיים';
    case 'CANCELLED': return 'בוטל';
    case 'NO_SHOW':   return 'לא התקיים';
    default:          return 'לא ידוע';
  }
};

/* ===================== Page ===================== */
const TeacherMonthlyLessonsReport = () => {
  const { user, showAlert } = useUser();
  const { getTeacherDoneLessonsByMonth } = useLessons();

  // Stable date — not recreated on every render
  const now = useRef(new Date()).current;
  const currentMonthStr = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}`;

  const [month,    setMonth]    = useState(currentMonthStr);
  const [loading,  setLoading]  = useState(false);
  const [lessons,  setLessons]  = useState([]);
  const [viewMode, setViewMode] = useState('table');

  const isTeacher = (user?.role || 'student') === 'teacher';

  useEffect(() => {
    if (!isTeacher) return;
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      try {
        const rows = await getTeacherDoneLessonsByMonth(month);
        if (cancelled) return;
        setLessons(Array.isArray(rows) ? rows : []);
      } catch (e) {
        if (cancelled) return;
        showAlert?.(e?.message || 'שגיאה בטעינת הדוח', 'error');
        setLessons([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [month, isTeacher, getTeacherDoneLessonsByMonth, showAlert]);

  const totals = useMemo(() => {
    const totalLessons             = lessons.length;
    const paid                     = lessons.reduce((s, l) => s + (Number(l.paidParticipants)        || 0), 0);
    // Real participant total, matching what the per-row column now
    // shows (participantsCount) — kept separate from `paid` above,
    // since that one deliberately excludes supplier-course lessons
    // for the "paying students" KPI and must stay that way. Used only
    // for the Excel export's total row, so the sheet's own rows
    // actually add up to the total shown for that column.
    const totalParticipants        = lessons.reduce((s, l) => s + (Number(l.participantsCount)       || 0), 0);
    const studentsPaidTotal        = lessons.reduce((s, l) => s + (Number(l.studentsPaidAmount)       || 0), 0);
    const teacherGrossTotal        = lessons.reduce((s, l) => s + (Number(l.teacherGrossAmount)       || 0), 0);
    const platformCommissionTotal  = lessons.reduce((s, l) => s + (Number(l.platformCommissionAmount) || 0), 0);
    const taxTotal                 = lessons.reduce((s, l) => s + (Number(l.taxAmount)               || 0), 0);
    const teacherNetTotal          = lessons.reduce((s, l) => s + (Number(l.teacherNetAmount)         || 0), 0);
    const avgParticipantsPerLesson = totalLessons > 0 ? paid / totalLessons : 0;
    const avgNetPerLesson          = totalLessons > 0 ? teacherNetTotal / totalLessons : 0;
    const commissionPercent        = teacherGrossTotal > 0 ? (platformCommissionTotal / teacherGrossTotal) * 100 : 0;
    return { totalLessons, paid, totalParticipants, studentsPaidTotal, teacherGrossTotal, platformCommissionTotal, taxTotal, teacherNetTotal, avgParticipantsPerLesson, avgNetPerLesson, commissionPercent };
  }, [lessons]);

  const exportToExcel = () => {
    try {
      if (!lessons.length) {
        showAlert?.('אין נתונים לייצוא עבור החודש הנבחר', 'error');
        return;
      }
      const rows = lessons.map((l) => ({
        'מזהה שיעור':        l.id ?? '',
        'תאריך':              (l.date || l.lesson_date || '').toString(),
        'שעת התחלה':          (l.startTime || l.start_time || '').toString().slice(0, 5),
        'שעת סיום':           (l.endTime   || l.end_time   || '').toString().slice(0, 5),
        'נושא / כותרת':       l.topic || l.title || l.lesson_topic || '',
        'קורס':               l.course_title || l.courseTitle || l.course_name || '',
        'סטטוס':              getReportStatusLabel(l),
        'מקור תשלום':         l.isSupplierCourse ? 'ישירות מול הספק' : 'פלטפורמה',
        'תלמידים משתתפים':    Number(l.participantsCount         || 0),
        'תשלומי תלמידים':    Number(l.studentsPaidAmount         || 0),
        'ברוטו למורה':        Number(l.teacherGrossAmount         || 0),
        'עמלת פלטפורמה':     Number(l.platformCommissionAmount   || 0),
        '% מס':              `${Number(l.taxPercent || 45).toFixed(0)}%`,
        'מס הכנסה':          Number(l.taxAmount                  || 0),
        'נטו לתשלום':        Number(l.teacherNetAmount            || 0),
        'מטבע':              l.currency || 'ILS',
      }));

      rows.push({
        'מזהה שיעור': 'סה"כ', 'תאריך': '', 'שעת התחלה': '', 'שעת סיום': '',
        'נושא / כותרת': '', 'קורס': '', 'סטטוס': '', 'מקור תשלום': '',
        'תלמידים משתתפים':   totals.totalParticipants,
        'תשלומי תלמידים':   Number(totals.studentsPaidTotal       || 0),
        'ברוטו למורה':       Number(totals.teacherGrossTotal       || 0),
        'עמלת פלטפורמה':    Number(totals.platformCommissionTotal  || 0),
        '% מס': '',
        'מס הכנסה':         Number(totals.taxTotal                 || 0),
        'נטו לתשלום':       Number(totals.teacherNetTotal          || 0),
        'מטבע': lessons[0]?.currency || 'ILS',
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [12,14,12,12,30,24,16,18,18,18,18,18,10,18,14,10].map((wch) => ({ wch }));
      ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { c: 0, r: 0 }, e: { c: 15, r: rows.length - 1 } }) };
      wb.Workbook = wb.Workbook || {};
      wb.Workbook.Views = [{ RTL: true }];
      XLSX.utils.book_append_sheet(wb, ws, 'דוח חודשי');
      XLSX.writeFileXLSX(wb, `doroos_teacher_monthly_report_${month}.xlsx`);
      showAlert?.('הדוח יוצא בהצלחה לקובץ Excel', 'success');
    } catch (e) {
      showAlert?.(e?.message || 'שגיאה בייצוא ל-Excel', 'error');
    }
  };

  /* ── Not a teacher ── */
  if (!isTeacher) {
    return (
      <>
        <TeacherMonthlyReportHeader />
        <div className="min-h-screen bg-gray-100 max-w-8xl mx-auto p-4 sm:p-6" dir="rtl">
          <div className="text-center py-12 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 11c0 1.657-1.343 3-3 3S6 12.657 6 11s1.343-3 3-3 3 1.343 3 3zm6 10H6a4 4 0 014-4h4a4 4 0 014 4z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">אין הרשאה</h3>
            <p className="text-gray-600">עמוד זה זמין למורים בלבד.</p>
          </div>
        </div>
      </>
    );
  }

  /* ── Main render ── */
  return (
    <div className="md:min-h-screen bg-gray-100">
      <TeacherMonthlyReportHeader />
      {loading && <SpinnerOverlay variant="glow" text="נא להמתין" dark={false} blur={false} />}

      {/* max-w-7xl is standard Tailwind; max-w-8xl was not */}
      <div className="mx-auto max-w-8xl px-3 sm:px-4 md:px-6 lg:px-8 pt-4 pb-8" dir="rtl">

        {/* ── Month picker ── */}
        <div className="mb-4 sm:mb-6">
          <MonthPicker
            value={month}
            onChange={(v) => setMonth(v)}
            maxDate={now}
            minYear={2023}
            locale="he"
          />
        </div>

        {/* ── KPI Cards ──
            2 cols xs, 3 cols md, 6 cols xl.
            At lg (1024px) we stay at 3 so each card has enough breathing room.
        */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">

          {/* 1 — שיעורים */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-3 sm:p-4 rounded-xl border border-green-200">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-green-700 truncate">מספר שיעורים</p>
                <p className="text-xl sm:text-2xl font-bold text-green-900">{totals.totalLessons}</p>
              </div>
            </div>
          </div>

          {/* 2 — תלמידים */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-3 sm:p-4 rounded-xl border border-blue-200">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-blue-700 truncate">תלמידים משלמים</p>
                <p className="text-xl sm:text-2xl font-bold text-blue-900">{totals.paid}</p>
              </div>
            </div>
          </div>

          {/* 3 — ברוטו */}
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-3 sm:p-4 rounded-xl border border-yellow-200">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-yellow-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-10V6m0 12v-2m9-4a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-yellow-700 truncate">הכנסות לפני עמלות</p>
                <p className="text-xl sm:text-2xl font-bold text-yellow-900">₪{money(totals.teacherGrossTotal)}</p>
              </div>
            </div>
          </div>

          {/* 4 — נטו */}
          <div className="bg-gradient-to-r from-purple-50 to-fuchsia-50 p-3 sm:p-4 rounded-xl border border-purple-200">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-purple-700 truncate">סכום לתשלום (נטו)</p>
                <p className="text-xl sm:text-2xl font-bold text-purple-900">₪{money(totals.teacherNetTotal)}</p>
              </div>
            </div>
          </div>

        </div>

        {/* ── Info banner ── */}
        <div className="mb-4 sm:mb-5 rounded-2xl border border-indigo-100 bg-indigo-50/70 px-4 py-3">
          <p className="text-sm font-semibold text-indigo-900">סיכום חודשי עבורך</p>
          <p className="text-xs sm:text-sm text-indigo-700 mt-1">
            הדוח מציג את כלל השיעורים בחודש שנבחר, כולל סטטוס כל שיעור, תשלומי תלמידים, עמלת הפלטפורמה,
            ניכוי המס והסכום נטו לתשלום. שיעורים בתוכניות של ספקים (מרכזי למידה) מוצגים גם הם, אך ללא
            חישוב הכנסה — התשלום עבורם מתבצע ישירות מול הספק.
          </p>
        </div>

        {/* ── View toggle + Export ── */}
        <div className="flex items-center justify-between gap-3 mb-3 sm:mb-4">
          <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
            {['table', 'cards'].map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-3 sm:px-5 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                  viewMode === mode
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {mode === 'table' ? 'טבלה' : 'כרטיסים'}
              </button>
            ))}
          </div>

          <button
            onClick={exportToExcel}
            disabled={loading || lessons.length === 0}
            className="px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            ייצוא ל-Excel
          </button>
        </div>

        {/* ── Empty state ── */}
        {lessons.length === 0 && !loading ? (
          <div className="text-center py-12 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M8 7V3m8 4V3M4 11h16M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">אין נתונים לחודש שנבחר</h3>
            <p className="text-gray-600 text-sm">נסה לבחור חודש אחר</p>
          </div>

        ) : viewMode === 'table' ? (
          /*
            Column visibility strategy — the 12-col table was always 1100px wide.
            Now columns are progressively disclosed:

            xs/sm  (< 768px)  : תאריך | שעה | נושא | סטטוס | נטו לתשלום
            md     (≥ 768px)  : + קורס + תלמידים משלמים
            lg     (≥ 1024px) : + תשלומי תלמידים + ברוטו + עמלה + % מס + מס הכנסה

            min-w tiers match visible column counts:
              xs  → min-w-[440px]
              md  → min-w-[640px]
              lg  → min-w-[1000px]
          */
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[440px] md:min-w-[640px] lg:min-w-[1000px] border-collapse">
                <thead>
                  <tr className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-100">
                    {/* Always visible */}
                    <th className="px-3 sm:px-4 py-3.5 text-right text-xs font-semibold text-indigo-700 whitespace-nowrap">תאריך</th>
                    <th className="px-3 sm:px-4 py-3.5 text-right text-xs font-semibold text-indigo-700 whitespace-nowrap">שעה</th>
                    <th className="px-3 sm:px-4 py-3.5 text-right text-xs font-semibold text-indigo-700 whitespace-nowrap">נושא</th>
                    {/* md+ */}
                    <th className="hidden md:table-cell px-3 sm:px-4 py-3.5 text-right text-xs font-semibold text-indigo-700 whitespace-nowrap">קורס</th>
                    {/* Always visible */}
                    <th className="px-3 sm:px-4 py-3.5 text-right text-xs font-semibold text-indigo-700 whitespace-nowrap">סטטוס</th>
                    {/* md+ */}
                    <th className="hidden md:table-cell px-3 sm:px-4 py-3.5 text-right text-xs font-semibold text-indigo-700 whitespace-nowrap">תלמידים</th>
                    {/* lg+ */}
                    <th className="hidden lg:table-cell px-3 sm:px-4 py-3.5 text-right text-xs font-semibold text-indigo-700 whitespace-nowrap">תשלומי תלמידים</th>
                    <th className="hidden lg:table-cell px-3 sm:px-4 py-3.5 text-right text-xs font-semibold text-indigo-700 whitespace-nowrap">ברוטו למורה</th>
                    <th className="hidden lg:table-cell px-3 sm:px-4 py-3.5 text-right text-xs font-semibold text-indigo-700 whitespace-nowrap">עמלת פלטפורמה</th>
                    <th className="hidden lg:table-cell px-3 sm:px-4 py-3.5 text-right text-xs font-semibold text-indigo-700 whitespace-nowrap">% מס</th>
                    <th className="hidden lg:table-cell px-3 sm:px-4 py-3.5 text-right text-xs font-semibold text-indigo-700 whitespace-nowrap">מס הכנסה</th>
                    {/* Always visible */}
                    <th className="px-3 sm:px-4 py-3.5 text-right text-xs font-semibold text-indigo-700 whitespace-nowrap">נטו לתשלום</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-50">
                  {lessons.map((lesson, idx) => {
                    const date                   = (lesson.date || lesson.lesson_date || '').toString();
                    const startTime              = (lesson.startTime || lesson.start_time || '').toString().slice(0, 5);
                    const endTime               = (lesson.endTime   || lesson.end_time   || '').toString().slice(0, 5);
                    const topic                 = lesson.topic || lesson.title || lesson.lesson_topic || '';
                    const course                = lesson.course_title || lesson.courseTitle || lesson.course_name || '';
                    const paid                  = Number(lesson.participantsCount        || 0);
                    const studentsPaidAmount    = Number(lesson.studentsPaidAmount         || 0);
                    const teacherGrossAmount    = Number(lesson.teacherGrossAmount         || 0);
                    const platformCommission    = Number(lesson.platformCommissionAmount   || 0);
                    const taxPercent            = Number(lesson.taxPercent || 45);
                    const taxAmount             = Number(lesson.taxAmount                  || 0);
                    const teacherNetAmount      = Number(lesson.teacherNetAmount            || 0);
                    const statusLabel           = getReportStatusLabel(lesson);
                    const isSupplierCourse      = !!lesson.isSupplierCourse;

                    return (
                      <tr
                        key={lesson.id || `${date}-${startTime}-${idx}`}
                        className={`${getRowStatusClass(lesson.reportStatus)} transition-colors duration-150`}
                      >
                        {/* תאריך */}
                        <td className="px-3 sm:px-4 py-3 text-xs text-gray-800 whitespace-nowrap font-medium">
                          {formatDate(date)}
                        </td>
                        {/* שעה */}
                        <td className="px-3 sm:px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                          {startTime}{endTime ? `–${endTime}` : ''}
                        </td>
                        {/* נושא */}
                        <td className="px-3 sm:px-4 py-3 text-xs text-gray-900">
                          <div className="flex items-center gap-1.5">
                            <div className="max-w-[140px] sm:max-w-[200px] truncate font-medium" title={topic}>
                              {topic || '—'}
                            </div>
                          </div>
                        </td>
                        {/* קורס — md+ */}
                        <td className="hidden md:table-cell px-3 sm:px-4 py-3 text-xs text-gray-500">
                          <div className="max-w-[160px] truncate" title={course}>{course || '—'}</div>
                        </td>
                        {/* סטטוס */}
                        <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
                          <StatusBadge status={lesson.reportStatus} label={statusLabel} />
                        </td>
                        {/* תלמידים — md+. Always real, even for a
                            supplier-course lesson — participantsCount
                            isn't zeroed the way paidParticipants is. */}
                        <td className="hidden md:table-cell px-3 sm:px-4 py-3">
                          <span className="inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-indigo-50 text-indigo-700 text-xs font-bold border border-indigo-100">
                            {paid}
                          </span>
                        </td>
                        {/* תשלומי תלמידים — lg+. Hidden for a supplier-
                            course lesson — payment amount is never
                            shown for these, regardless of participant
                            count. */}
                        <td className="hidden lg:table-cell px-3 sm:px-4 py-3 whitespace-nowrap">
                          {isSupplierCourse ? <span className="text-xs text-gray-400">—</span> : <MoneyCell value={money(studentsPaidAmount)} color="blue" />}
                        </td>
                        {/* ברוטו — lg+ */}
                        <td className="hidden lg:table-cell px-3 sm:px-4 py-3 whitespace-nowrap">
                          {isSupplierCourse ? <span className="text-xs text-gray-400">—</span> : <MoneyCell value={money(teacherGrossAmount)} color="green" />}
                        </td>
                        {/* עמלה — lg+ */}
                        <td className="hidden lg:table-cell px-3 sm:px-4 py-3 whitespace-nowrap">
                          {isSupplierCourse ? <span className="text-xs text-gray-400">—</span> : <MoneyCell value={money(platformCommission)} color="amber" />}
                        </td>
                        {/* % מס — lg+ */}
                        <td className="hidden lg:table-cell px-3 sm:px-4 py-3 text-xs text-gray-500 whitespace-nowrap text-center">
                          {isSupplierCourse ? '—' : `${taxPercent.toFixed(0)}%`}
                        </td>
                        {/* מס הכנסה — lg+ */}
                        <td className="hidden lg:table-cell px-3 sm:px-4 py-3 whitespace-nowrap">
                          {isSupplierCourse ? <span className="text-xs text-gray-400">—</span> : <MoneyCell value={money(taxAmount)} color="rose" />}
                        </td>
                        {/* נטו — always */}
                        <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
                          {isSupplierCourse ? (
                            <SupplierPaidBadge />
                          ) : (
                            <MoneyCell value={money(teacherNetAmount)} color="purple" bold />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

        ) : (
          /* ── Cards view — already responsive (1 col xs, 2 col sm+) ── */
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-4">
            {lessons.map((lesson, idx) => {
              const date                 = (lesson.date || lesson.lesson_date || '').toString();
              const startTime            = (lesson.startTime || lesson.start_time || '').toString().slice(0, 5);
              const endTime             = (lesson.endTime   || lesson.end_time   || '').toString().slice(0, 5);
              const topic               = lesson.topic || lesson.title || lesson.lesson_topic || '';
              const course              = lesson.course_title || lesson.courseTitle || lesson.course_name || '';
              const paid                = Number(lesson.participantsCount        || 0);
              const studentsPaidAmount  = Number(lesson.studentsPaidAmount         || 0);
              const teacherGrossAmount  = Number(lesson.teacherGrossAmount         || 0);
              const platformCommission  = Number(lesson.platformCommissionAmount   || 0);
              const taxPercent          = Number(lesson.taxPercent || 45);
              const taxAmount           = Number(lesson.taxAmount                  || 0);
              const teacherNetAmount    = Number(lesson.teacherNetAmount            || 0);
              const statusLabel         = getReportStatusLabel(lesson);
              const isSupplierCourse    = !!lesson.isSupplierCourse;

              return (
                <div
                  key={lesson.id || `${date}-${startTime}-${idx}`}
                  className="bg-white rounded-2xl border border-gray-100 shadow-md overflow-hidden"
                >
                  <div className={`h-1 w-full ${getStatusAccentBarClass(lesson.reportStatus)}`} />

                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <p className="text-sm font-semibold text-gray-900 truncate leading-snug" title={topic}>
                        {topic || '—'}
                      </p>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {isSupplierCourse && <SupplierPaidBadge compact />}
                        <StatusBadge status={lesson.reportStatus} label={statusLabel} />
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-0.5">
                      <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                        <rect x="3" y="4" width="18" height="18" rx="2" />
                        <path d="M16 2v4M8 2v4M3 10h18" />
                      </svg>
                      <span>{formatDate(date)}</span>
                      {startTime && (
                        <>
                          <span className="text-gray-300">·</span>
                          <span>{startTime}{endTime ? `–${endTime}` : ''}</span>
                        </>
                      )}
                    </div>

                    {course && (
                      <p className="text-xs text-gray-400 truncate mb-1" title={course}>{course}</p>
                    )}

                    <div className="border-t border-gray-100 mt-3 pt-3 grid grid-cols-2 gap-2">
                      {/* Always shown, even for a supplier-course
                          lesson — participantsCount is a real count,
                          unlike the payment amount next to it. */}
                      <div className="bg-indigo-50 rounded-xl p-2.5">
                        <p className="text-[10px] text-indigo-500 mb-0.5">תלמידים משתתפים</p>
                        <p className="text-sm font-bold text-indigo-700">{paid}</p>
                      </div>
                      {!isSupplierCourse && (
                        <div className="bg-blue-50 rounded-xl p-2.5">
                          <p className="text-[10px] text-blue-500 mb-0.5">תשלומי תלמידים</p>
                          <p className="text-sm font-bold text-blue-700">₪{money(studentsPaidAmount)}</p>
                        </div>
                      )}
                      {isSupplierCourse ? (
                        <div className="col-span-2 bg-slate-50 rounded-xl p-2.5 border border-slate-100">
                          <p className="text-[10px] text-slate-500 mb-0.5">סכום לתשלום</p>
                          <p className="text-sm font-bold text-slate-600">משולם ע״י הספק</p>
                          <p className="text-[10px] text-slate-400 mt-0.5">שיעור בתוכנית של ספק — התשלום מתבצע ישירות מול הספק, מחוץ למערכת</p>
                        </div>
                      ) : (
                        <>
                          <div className="bg-green-50 rounded-xl p-2.5">
                            <p className="text-[10px] text-green-500 mb-0.5">ברוטו למורה</p>
                            <p className="text-sm font-bold text-green-700">₪{money(teacherGrossAmount)}</p>
                          </div>
                          <div className="bg-amber-50 rounded-xl p-2.5">
                            <p className="text-[10px] text-amber-500 mb-0.5">עמלת פלטפורמה</p>
                            <p className="text-sm font-bold text-amber-700">₪{money(platformCommission)}</p>
                          </div>
                          <div className="bg-rose-50 rounded-xl p-2.5">
                            <p className="text-[10px] text-rose-500 mb-0.5">מס הכנסה ({taxPercent.toFixed(0)}%)</p>
                            <p className="text-sm font-bold text-rose-700">₪{money(taxAmount)}</p>
                          </div>
                          <div className="bg-gradient-to-l from-purple-50 to-fuchsia-50 rounded-xl p-2.5 border border-purple-100">
                            <p className="text-[10px] text-purple-600 mb-0.5">סכום לתשלום</p>
                            <p className="text-base font-extrabold text-purple-700">₪{money(teacherNetAmount)}</p>
                            <p className="text-[10px] text-purple-500 mt-0.5">לאחר עמלות וניכוי מס</p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherMonthlyLessonsReport;